import React, { useState, useEffect } from 'react';
import HeadingCard from '../../../common/element/Card/HeadingCard';
import Footer from '../Footer';
import ThreeDotLoader from '../../../common/element/ButtonLoader/ThreeDotLoader';

const QuestionPage = ({
  questions,
  answers,
  allAnswers,
  onAnswerChange,
  onNext,
  onPrevious,
  isFirst,
  isLast,
  currentStep,
  totalSteps,
  Button,
  isSubmitting = false
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState({
    question1: {
      priority1: '',
      priority2: ''
    },
    question2: {
      priority1: '',
      priority2: ''
    }
  });

  // Initialize with existing answers when component mounts or step changes
  useEffect(() => {
    if (answers && Object.keys(answers).length > 0) {
      setSelectedAnswers({
        question1: {
          priority1: answers.question1?.priority1 || '',
          priority2: answers.question1?.priority2 || ''
        },
        question2: {
          priority1: answers.question2?.priority1 || '',
          priority2: answers.question2?.priority2 || ''
        }
      });
    } else {
      setSelectedAnswers({
        question1: { priority1: '', priority2: '' },
        question2: { priority1: '', priority2: '' }
      });
    }
  }, [currentStep, answers]);

  const handleAnswerSelect = (questionKey, optionId) => {
    setSelectedAnswers(prev => {
      const newAnswers = { ...prev };
      const currentQuestion = { ...newAnswers[questionKey] };

      // If clicking on already selected P1 - clear both P1 and P2
      if (currentQuestion.priority1 === optionId) {
        newAnswers[questionKey] = {
          priority1: '',
          priority2: ''
        };
      }
      // If clicking on already selected P2 - clear only P2
      else if (currentQuestion.priority2 === optionId) {
        newAnswers[questionKey] = {
          priority1: currentQuestion.priority1,
          priority2: ''
        };
      }
      // If no P1 selected yet - set as P1
      else if (!currentQuestion.priority1) {
        newAnswers[questionKey] = {
          priority1: optionId,
          priority2: currentQuestion.priority2
        };
      }
      // If P1 exists but no P2, and clicking different option - set as P2
      else if (currentQuestion.priority1 && !currentQuestion.priority2) {
        newAnswers[questionKey] = {
          priority1: currentQuestion.priority1,
          priority2: optionId
        };
      }
      // If both P1 and P2 exist, clicking new option - replace P2
      else if (currentQuestion.priority1 && currentQuestion.priority2) {
        newAnswers[questionKey] = {
          priority1: currentQuestion.priority1,
          priority2: optionId
        };
      }

      // Call parent update function immediately with the new answers
      setTimeout(() => onAnswerChange(newAnswers), 0);

      return newAnswers;
    });
  };

  // Check if all questions have both P1 and P2 selected
  const isNextDisabled = !selectedAnswers.question1.priority1 ||
    !selectedAnswers.question1.priority2 ||
    !selectedAnswers.question2.priority1 ||
    !selectedAnswers.question2.priority2;

  const renderQuestion = (question, questionKey, questionNumber) => {
    const currentAnswers = selectedAnswers[questionKey];

    return (
      <div key={questionKey} className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-6">
          Question {questionNumber} :- {question.question}
        </h3>

        {/* Options Grid - 2 columns, 3 rows */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {question.options.map((option, index) => {
            const isPriority1 = currentAnswers.priority1 === option.id;
            const isPriority2 = currentAnswers.priority2 === option.id;
            const isSelected = isPriority1 || isPriority2;

            return (
              <div
                key={option.id}
                className={`p-4 rounded-[12px] cursor-pointer transition-all duration-200 border relative min-h-[80px] flex items-center justify-center text-center ${isSelected
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-800 border-gray-300 hover:border-orange-300'
                  }`}
                onClick={() => handleAnswerSelect(questionKey, option.id)}
              >
                <span className="font-medium text-sm leading-tight px-2">{option.text}</span>

                {/* Priority Badge */}
                {isPriority1 && (
                  <div className="absolute top-2 right-2">
                    <span className="bg-white text-orange-500 px-2 py-1 rounded text-xs font-bold">
                      P1
                    </span>
                  </div>
                )}
                {isPriority2 && (
                  <div className="absolute top-2 right-2">
                    <span className="bg-white text-orange-500 px-2 py-1 rounded text-xs font-bold">
                      P2
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Calculate question numbers based on actual question IDs from API
  const getQuestionNumber = (questionIndex) => {
    if (questions && questions[questionIndex]) {
      return questions[questionIndex].id;
    }
    // Fallback to calculated number if ID is not available
    return ((currentStep - 1) * 2) + questionIndex + 1;
  };

  // Return how many questions attempted (out of 2) for a given step number
  const getAttemptedForStep = (stepNum) => {
    const stepAnswers = allAnswers?.[stepNum];
    if (!stepAnswers) return 0;
    let count = 0;
    if (stepAnswers.question1?.priority1 && stepAnswers.question1?.priority2) count++;
    if (stepAnswers.question2?.priority1 && stepAnswers.question2?.priority2) count++;
    return count;
  };

  // Calculate total questions for display
  const getTotalQuestions = () => totalSteps * 2;
  const attemptedCount = Object.keys(allAnswers || {}).reduce((acc, stepNumber) => {
    const stepAnswers = allAnswers[stepNumber];
    if (!stepAnswers) return acc;
    if (stepAnswers.question1?.priority1 && stepAnswers.question1?.priority2) acc++;
    if (stepAnswers.question2?.priority1 && stepAnswers.question2?.priority2) acc++;
    return acc;
  }, 0);

  const firstQuestionNumber = getQuestionNumber(0);
  const secondQuestionNumber = getQuestionNumber(1);

  return (
    <div >
      {/* Header with orange bar */}
      {/* <div className="bg-orange-500 text-white px-6 py-5 rounded-t-lg flex justify-between items-center">
        <div className="font-semibold">Inner Drives Assessment : Test</div>
        <div className="text-sm">Attempted Questions: {Math.min(attemptedCount, getTotalQuestions())}/{getTotalQuestions()}</div>
      </div> */}
      <HeadingCard
        title={
          <div className="flex justify-between items-center w-full">
            <span className="font-lato not-italic font-bold text-[18px] md:text-[24px]  leading-[115%] text-white">Inner Drives Assessment </span>
            <div className="font-lato not-italic font-bold text-[16px] md:text-[24px] leading-[115%] text-white">Attempted Questions: {Math.min(attemptedCount, getTotalQuestions())}/{getTotalQuestions()}</div>
          </div>
        }
      > <div className="">
          {/* Each question wrapped in a bordered card area */}
          {questions && questions.length >= 2 && (
            <>
              <div className="mb-6 border border-[#FFD7A1] rounded-[12px] p-[18px]">
                <div className="flex items-center justify-between  mb-[28px]">
                  <h3 className="font-lato not-italic font-bold text-[18px] md:text-[20px] text-start md:text-center leading-[115%] text-black">Question {firstQuestionNumber} : {questions[0].question}</h3>
                  {/* <span className="text-sm bg-gray-100 text-gray-700 px-2 py-1 rounded">{getAttemptedForStep(currentStep)}/2</span> */}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {questions[0]?.options?.map((option, idx) => {
                    const isPriority1 = selectedAnswers.question1.priority1 === option.id;
                    const isPriority2 = selectedAnswers.question1.priority2 === option.id;
                    const isSelected = isPriority1 || isPriority2;
                    return (
                      <div
                        key={option.id}
                        className={`p-3 md:p-4 rounded-[12px] cursor-pointer transition-all duration-200 border relative min-h-[56px] flex items-center justify-center text-center ${isSelected
                            ? 'bg-[#FF8E1C] text-white border-[#FF8E1C]'
                            : 'bg-white text-gray-800 border-gray-200 hover:border-[#FF8E1C]-300'
                          }`}
                        onClick={() => handleAnswerSelect('question1', option.id)}
                      >
                        <span className="font-lato not-italic font-semibold text-[14px] leading-[17px] px-2">
                          <span className="mr-2 font-bold">{idx + 1}.</span>{option.text}
                        </span>
                        {isPriority1 && (<div className="top-1/2 right-4"><span className="bg-[#FFD7A1] text-black w-[20px] h-[20px] flex justify-center items-center rounded-full font-lato not-italic font-medium text-[12px] leading-[150%] text-black">P1</span></div>)}
                        {isPriority2 && (<div className="top-1/2 right-4"><span className="bg-[#FFD7A1] text-black w-[20px] h-[20px] flex justify-center items-center rounded-full font-lato not-italic font-medium text-[12px] leading-[150%] text-black">P2</span></div>)}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border border-[#FFD7A1] rounded-[12px] p-4">
                <div className="flex items-center justify-between mb-[28px]">
                  <h3 className="font-lato not-italic font-bold text-[18px] md:text-[20px] text-start md:text-center leading-[115%] text-black">Question {secondQuestionNumber} : {questions[1].question}</h3>
                  {/* <span className="text-sm bg-gray-100 text-gray-700 px-2 py-1 rounded">{getAttemptedForStep(currentStep)}/2</span> */}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {questions[1].options?.map((option, idx) => {
                    const isPriority1 = selectedAnswers.question2.priority1 === option.id;
                    const isPriority2 = selectedAnswers.question2.priority2 === option.id;
                    const isSelected = isPriority1 || isPriority2;
                    return (
                      <div
                        key={option.id}
                        className={`p-3 md:p-4 rounded-[12px] cursor-pointer transition-all duration-200 border relative min-h-[56px] flex items-center justify-center text-center ${isSelected
                            ? 'bg-[#FF8E1C] text-white border-[#FF8E1C]'
                            : 'bg-white text-gray-800 border-gray-200 hover:border-[#FF8E1C]-300'
                          }`}
                        onClick={() => handleAnswerSelect('question2', option.id)}
                      >
                        <span className="font-lato not-italic font-semibold text-[14px] leading-[17px] px-2">
                          <span className="mr-2 font-bold">{idx + 1}.</span>{option.text}
                        </span>
                        {isPriority1 && (<div className="top-1/2 right-4"><span className="bg-[#FFD7A1] text-black w-[20px] h-[20px] flex justify-center items-center rounded-full font-lato not-italic font-medium text-[12px] leading-[150%] text-black">P1</span></div>)}
                        {isPriority2 && (<div className="top-1/2 right-4"><span className="bg-[#FFD7A1] text-black w-[20px] h-[20px] flex justify-center items-center rounded-full font-lato not-italic font-medium text-[12px] leading-[150%] text-black">P2</span></div>)}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Validation and navigation same as before */}
          {/* {isNextDisabled && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-[12px] p-4 mb-6">
            <div className="flex items-center">
              <span className="text-yellow-600 mr-2">⚠️</span>
              <p className="text-yellow-800 text-sm font-medium">
                Please select both Priority 1 (P1) and Priority 2 (P2) for each question before proceeding.
              </p>
            </div>
          </div>
        )} */}


        </div></HeadingCard>
      <div className="flex justify-center items-center mt-8">
        <div>
          {!isFirst && (
            <Button
              onClick={onPrevious}
              className="bg-white text-black px-[18px] py-4 w-[150px] h-14 rounded-[12px] font-lato not-italic font-bold text-[16px] leading-[150%] mr-4"
            >
              Back
            </Button>
          )}
          <Button
            onClick={onNext}
            disabled={isNextDisabled || isSubmitting}
            className={`px-[18px] py-4 w-[150px] h-14 rounded-[12px] font-lato not-italic font-bold text-[16px] leading-[150%] transition-all duration-200 ${isNextDisabled || isSubmitting
                ? 'opacity-50 cursor-not-allowed bg-gray-300 text-gray-500'
                : 'bg-[#FF8E1C] hover:bg-[#FF8E1C] text-white shadow-[0px_2px_5px_#FFF4E5] hover:shadow-[0px_2px_2px_#FFF4E5]'
              }`}
          >
            {isLast && isSubmitting ? <ThreeDotLoader /> : (isLast ? 'Submit' : 'Next ')}
          </Button>
        </div>
       
      </div>
    </div>
  );
};

export default QuestionPage;